import type { Method, ParticipantBehaviourStatus } from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  FloatingPopover,
  MultiSelect,
  type MultiSelectOption,
  NumberedBadge,
  type OnChangeRequest,
} from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaCaretDown, FaFilter } from 'react-icons/fa';

import { FormStack } from '../../../components/forms/FormStack';
import type { ParticipantBehavioursFilter } from '../hooks/useParticipantBehavioursFilter';
import { methodOptions } from './MethodTag';
import { participantBehaviourStatusOptions } from './ParticipantBehaviourStatusTag';

interface Props {
  filter: ParticipantBehavioursFilter;
}

const ParticipantBehaviourNameFilterControl = (props: Props) => {
  const [value, setValue] = React.useState(props.filter.nameFilter);

  const onChange = React.useCallback(
    (value: string) => {
      setValue(value);
      props.filter.onNameFilter(value);
    },
    [props.filter],
  );

  React.useEffect(() => {
    setValue(props.filter.nameFilter);
  }, [props.filter.nameFilter]);

  return (
    <ChakraV3.Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder='All behaviours'
      size='md'
    />
  );
};

const ParticipantBehaviourStatusFilterControl = (props: Props) => {
  const options: MultiSelectOption<ParticipantBehaviourStatus>[] = Object.values(
    participantBehaviourStatusOptions,
  );

  const selectedValues: MultiSelectOption<ParticipantBehaviourStatus>[] = React.useMemo(() => {
    return (props.filter.statusFilter ?? []).flatMap((status) => {
      return options.find((o) => P.isEqual(o.raw, status)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<ParticipantBehaviourStatus>) => {
      props.filter.onStatusFilter(req.values.map(({ raw }) => raw));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All statuses'
      optionVariant='tag'
    />
  );
};

const ParticipantBehaviourMethodFilterControl = (props: Props) => {
  const options: MultiSelectOption<Method['__typename']>[] = Object.values(methodOptions);

  const selectedValues: MultiSelectOption<Method['__typename']>[] = React.useMemo(() => {
    return (props.filter.methodFilter ?? []).flatMap((status) => {
      return options.find((o) => P.isEqual(o.raw, status)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<Method['__typename']>) => {
      props.filter.onMethodFilter(req.values.map(({ raw }) => raw));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All methods'
      optionVariant='tag'
    />
  );
};

export const ParticipantBehaviourFilterControls = (props: Props) => {
  return (
    <FormStack orientation='horizontal'>
      <ChakraV3.Stack maxW='2xs'>
        <ParticipantBehaviourNameFilterControl filter={props.filter} />
      </ChakraV3.Stack>
      <FloatingPopover
        render={() => (
          <ChakraV3.Stack bg='white' gap={0} w='xs' shadow='2xl' rounded='md' overflow='hidden'>
            <ChakraV3.HStack py={2} px={4} borderBottomWidth='1px'>
              <ChakraV3.Text fontWeight='semibold'>Filters</ChakraV3.Text>
              <ChakraV3.Spacer />
              <NumberedBadge count={props.filter.activeFilterCount} />
            </ChakraV3.HStack>
            <FormStack py={4} px={4} orientation='vertical'>
              <ChakraV3.FormField label='Status'>
                <ParticipantBehaviourStatusFilterControl filter={props.filter} />
              </ChakraV3.FormField>
              <ChakraV3.FormField label='Methods'>
                <ParticipantBehaviourMethodFilterControl filter={props.filter} />
              </ChakraV3.FormField>
              <ChakraV3.Separator />

              <ChakraV3.Button
                w='fit-content'
                size='sm'
                variant='outline'
                disabled={props.filter.activeFilterCount === 0}
                onClick={props.filter.reset}
                colorPalette='brand'
              >
                Reset
              </ChakraV3.Button>
            </FormStack>
          </ChakraV3.Stack>
        )}
      >
        <ChakraV3.Button size='sm' variant='outline' lineHeight='unset'>
          <FaFilter />
          <ChakraV3.HStack>
            Filter
            <ChakraV3.Stack w='28px'>
              <NumberedBadge count={props.filter.activeFilterCount} />
            </ChakraV3.Stack>
          </ChakraV3.HStack>
          <FaCaretDown />
        </ChakraV3.Button>
      </FloatingPopover>
    </FormStack>
  );
};
